/**
 * The pure model of the Story panel and the relation list (1.J deliverable 3). No React, no
 * requests: every rule a spec pins lives here.
 */
import { ApiError, type CanonStatus, type DocTitleV1, type LinkV1 } from '@codex/contracts';

/** What a row needs to know about its page besides its title. */
export interface RowFacts {
  readonly canonStatus: CanonStatus | null;
  /** The page's primary parent, null when it has no home. */
  readonly homeId: string | null;
}

export type HomeTag = 'here' | 'elsewhere' | 'none';

export interface StoryRow {
  readonly link: LinkV1;
  /** The page at the other end. */
  readonly docId: string;
  readonly title: string;
  readonly typeKey: string;
  /** `reference` pages carry the SRD badge. */
  readonly library: string;
  readonly isStub: boolean;
  readonly canonStatus: CanonStatus | null;
  readonly home: HomeTag;
  readonly optional: boolean;
}

/** The page at the other end of `link` as seen from `docId`. */
export function otherEnd(link: Pick<LinkV1, 'sourceId' | 'targetId'>, docId: string): string {
  return link.targetId === docId ? link.sourceId : link.targetId;
}

export const OPTIONAL_ROLE = 'optional';

export function isOptional(link: Pick<LinkV1, 'role'>): boolean {
  return link.role === OPTIONAL_ROLE;
}

/** The "Optional" toggle is offered only when the role is empty or already 'optional'. */
export function canToggleOptional(link: Pick<LinkV1, 'role'>): boolean {
  return link.role === null || link.role === OPTIONAL_ROLE;
}

export function optionalPatch(link: Pick<LinkV1, 'role'>): { role: string | null } {
  return isOptional(link) ? { role: null } : { role: OPTIONAL_ROLE };
}

/**
 * The live outgoing `contains` links of a page in `links.list` order. A row whose page is
 * trashed, merged, missing or not yet read is left out. A row is 'elsewhere' only when its page's
 * home is another page than `pageId`.
 */
export function storyRows(
  links: readonly LinkV1[],
  titles: ReadonlyMap<string, DocTitleV1>,
  facts: ReadonlyMap<string, RowFacts>,
  pageId: string,
): StoryRow[] {
  const rows: StoryRow[] = [];
  for (const link of links) {
    const title = titles.get(link.targetId);
    if (title === undefined || title.state !== 'live') continue;
    const known = facts.get(link.targetId);
    let home: HomeTag = 'none';
    if (link.isPrimary) home = 'here';
    else if (known?.homeId && known.homeId !== pageId) home = 'elsewhere';
    rows.push({
      link,
      docId: link.targetId,
      title: title.title,
      typeKey: title.typeKey,
      library: title.library,
      isStub: title.isStub,
      canonStatus: known?.canonStatus ?? null,
      home,
      optional: isOptional(link),
    });
  }
  return rows;
}

/**
 * The `afterLinkId` for moving the row at `index` by -1 or +1 (null puts it first), or
 * `undefined` at either end: nothing is sent.
 */
export function afterIdForMove(
  rows: readonly { readonly link: { readonly id: string } }[],
  index: number,
  delta: -1 | 1,
): string | null | undefined {
  const target = index + delta;
  if (index < 0 || index >= rows.length || target < 0 || target >= rows.length) return undefined;
  if (delta === -1) return target === 0 ? null : (rows[target - 1]?.link.id ?? null);
  return rows[target]?.link.id;
}

/** The rows after moving the one at `from` to index `to`. */
export function reorderTo<T>(rows: readonly T[], from: number, to: number): T[] {
  const next = [...rows];
  const [row] = next.splice(from, 1);
  if (row !== undefined) next.splice(to, 0, row);
  return next;
}

/** `quest` under an `arc`, `script` under a `quest`, else `note` (DECISIONS 98 item 20). */
export function childTypeFor(typeKey: string): string {
  if (typeKey === 'arc') return 'quest';
  if (typeKey === 'quest') return 'script';
  return 'note';
}

/** Structural types always show the panel; any other page once it has a `contains` link. */
export function showPanel(meta: { readonly capabilities: readonly string[] }, rowCount: number) {
  return meta.capabilities.includes('structural') || rowCount > 0;
}

/**
 * `isPrimary` for a picked page: true only for PLAN section 2's pairs, a quest with no home on an
 * arc and a script with no home on a quest. An NPC, a place or a note keeps its place elsewhere.
 */
export function addPlan(
  pageType: string,
  picked: { readonly typeKey: string; readonly homeId: string | null },
): { isPrimary: boolean } {
  if (picked.homeId !== null) return { isPrimary: false };
  return {
    isPrimary:
      (pageType === 'arc' && picked.typeKey === 'quest') ||
      (pageType === 'quest' && picked.typeKey === 'script'),
  };
}

/** A refused primary create worth one more try without a home. */
export function retryWithoutHome(error: unknown): boolean {
  return error instanceof ApiError && error.status === 422 && error.code === 'link_cycle';
}

export const RELATION_COLUMNS = ['label', 'role', 'note', 'canon'] as const;
export type RelationColumn = (typeof RELATION_COLUMNS)[number];

/** The known columns in the given order, unknown ones dropped, `label` and `note` by default. */
export function relationColumns(attr: readonly string[]): RelationColumn[] {
  const known = attr.filter((key, i): key is RelationColumn => {
    return (RELATION_COLUMNS as readonly string[]).includes(key) && attr.indexOf(key) === i;
  });
  return known.length > 0 ? known : ['label', 'note'];
}

/** The error code a notice or retry rule reads: an `ApiError`'s, else `'network'`. */
export function codeOf(error: unknown): string {
  return error instanceof ApiError ? error.code : 'network';
}
